import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { Auth } from 'aws-amplify';
import { SessionExpiredError, useSessionExpiry } from '@/composables/useSessionExpiry';
import { DateTime } from 'luxon';
import {
  CarrierLabelValue,
  CategoryValueSeriesWithSourceAgencyKey,
  CommissionValue,
  ExposureLabelValue,
  LabelValue,
} from '@/types/label-value';
import { AccountSummary, AccountInfo, ClientAccount, AccountServicingRole } from '@/types/AccountSummary';
import {
  Account,
  AccountAttribute,
  AccountCard,
  AccountPeer,
  AccountUpdateResponse,
  ParentAccountCompany,
  PublicCompanyData,
  PublicCompanySearchResult,
} from '@/types/Account';
import {
  Carrier,
  CarrierFinancials,
  CarrierGroup,
  CarrierPremiumByWholesaler,
  CarrierPremiumByAgency,
  IssuingCompany,
  CarrierShareOfWallet,
  CarrierPoliciesByIdParams,
  CarrierTopClients,
} from '@/types/Carrier';
import {
  AccountBenchmark,
  CrimeBenchmark,
  CyberBenchmark,
  DOBenchmark,
  EPLBenchmark,
  FiduciaryBenchmark,
  UmbrellaCarrierBenchmark,
  UmbrellaTotalLimit,
} from '@/types/Benchmark';
import { Industry, TopLevelClientIndustry, ClientIndustry } from '@/types/Industry';
import { StockExchange } from '@/types/StockExchange';
import { AccountPatchRequest } from '@/types/AccountPatchRequest';
import { EpicPolicy } from '@/types/tower/Domain';
import { AccountKPI } from '@/types/AccountKPI';
import { MultiSeries, MultiSeriesDataset, MultiSeriesLabel } from '@/types/MultiSeries';
import { CarrierKPI } from '@/types/CarrierKPI';
import { CarrierSurveyResult } from '@/types/CarrierSurvey';
import {
  CurrentPolicyLineOverride,
  CurrentPolicyOverride,
  PolicyOverride,
  PolicyLineOverride,
} from '@/types/PolicyOverrides';
import { AuditLog } from '@/types/AuditLog';
import {
  ExposureMetric,
  ExposureOption,
  ExposureProperty,
  ExposurePropertyRefData,
  ExposureValue,
  ExposureValueChangeEntry,
  ExposureValueEntry,
} from '@/types/AccountExposure';
import { Claim } from '@/types/Claim';
import { ExternalPolicy } from '@/types/PolicyCBO';
import { Sankey, SankeyLink, SankeyNode, SankeyDataNode } from '@/types/Sankey';
import { chartMutedColor, colorsBySourceAgencyKey, paletteColors } from '@/utilities/Color';
import { LineStatus, LineStatusUpdateRequest } from '@/types/LineStatus';
import { UpdateCountryRequest, UpdateGroupRequest, WholesalerAdmin } from '@/types/WholesalerAdmin';
import { Quote, QuoteItemType, QuoteLayer, QuoteProgram, QuoteRefDataType, QuoteSequenceRequest } from '@/types/Quote';
import {
  Wholesaler,
  WholesalerGroup,
  WholesalerGroupKPI,
  WholesalerGroupPoliciesByIdParams,
  WholesalerGroupSelection,
  WholesalerPremiumByAgency,
  WholesalerPremiumByCarrier,
} from '@/types/WholesalerGroup';
import { VolatilityModel } from '@/types/VolatilityModel';
import { ExternalUser, HomepagePreferencePatchRequest, User, UserProfile, UserProfilePatchRequest } from '@/types/User';
import { PersonalKPI } from '@/types/Personal';
import { Agency } from '@/types/Agency';
import {
  CreateProgramRequest,
  PortcoProgram,
  Program,
  ProgramDetails,
  ProgramInfo,
  ProgramPolicy,
  ProgramSummary,
} from '@/types/Programs';
import { Syndicate } from '@/types/Syndicate';
import { PolicySublimit, PolicySubretention, SubitemTypeRef } from '@/types/PolicySublimit';
import { PoliciesResponse, PolicyStats } from '@/utilities/Policies';
import { AllowedPermissionsByRole, AppRole, UserPermissionDetails } from '@/types/UserPermissions';
import { ProgramRate as ProgramRate } from '@/types/ProgramRates';
import { AccountContact } from '@/types/AccountContact';
import { transformMapToShowTop, createSankeyViewNodes, agencyLegendOrder } from '@/utilities/Charts';
import { PolicySource } from '@/types/PolicySource';
import { CreateTableauRequest, TableauDashboard, TableauType, UpdateTableauRequest } from '@/types/TableauDashboard';
import { PolicyLine } from '@/types/PolicyLine';
import { IdValue } from '@/types/id-value';
import {
  AnalysisRun,
  CompanyActualLimitsAndRetention,
  CompanyExposureAccount,
  CurrentVSAggregateExposure,
  CyberModelDetails,
} from '@/types/CyberCube';
import Store from '@/store';
import { AccountProgramData, AccountFinancials } from '@/types/Analytics';
import { StockDropFactor, StockDropModel, StockDropSettlement } from '@/types/StockDropModel';
import { ProfitCenter } from '@/types/ProfitCenter.js';
import { UniversalSearchResult, UniversalSearchParams } from '@/types/UniversalSearch';
import { Product } from '@/types/Product';
import { ExternalPolicyEntityOptions } from '@/types/ExternalPolicyBinder';

export interface ExternalPolicyUploadResponse {
  validPolicy: boolean;
  messages: string[];
}

export function isAxiosError(result: AxiosResponse | AxiosError): result is AxiosError {
  return (result as AxiosError).isAxiosError !== undefined;
}

export function getArrayQueryString<T = string | number>(queryParam: string, values: T[] | undefined): string {
  if (values && values.length > 0) {
    return values.map((x) => `${queryParam}=${encodeURIComponent(String(x).valueOf())}`).join('&');
  }
  return '';
}

function convertToQueryString(queryParams: QueryParams): string {
  const queries: string[] = [];
  const record: Record<string, string> = {};
  for (const key of Object.entries(queryParams)) {
    if (Array.isArray(key[1])) {
      if (key[1].length > 0) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        queries.push(getArrayQueryString(key[0], key[1]));
      }
    } else if (key[1] !== undefined && key[1] !== null) {
      record[key[0]] = key[1].toString();
    }
  }
  const urlParamStr = new URLSearchParams(record).toString();
  if (urlParamStr !== '') {
    queries.push(urlParamStr);
  }
  return queries.join('&');
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const correlationId = Store.state?.user?.correlationId;
  // Establish liveness before Amplify can see a dead session: currentSession() reacts to
  // one by redirecting to the Cognito logout endpoint, which would replace the modal. The
  // probe costs nothing while the cached ID token is still valid.
  await useSessionExpiry().probeSession();
  if (Store.state.sessionExpired) {
    throw new SessionExpiredError();
  }

  try {
    const session = await Auth.currentSession();
    const idToken = session.getIdToken().getJwtToken();
    return {
      Authorization: `Bearer ${idToken}`,
      'X-Correlation-ID': correlationId ?? '',
    };
  } catch {
    return {
      Authorization: '',
      'X-Correlation-ID': correlationId ?? '',
    };
  }
}

export type QueryParams = Record<string, string | number | number[] | string[] | boolean | undefined | DateTime | null>;

export class Service {
  axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: import.meta.env.VITE_API_URL,
      headers: { 'Content-Type': 'application/json' },
    });

    if (!import.meta.env.VITE_E2E) {
      this.axiosInstance.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
          const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
          // A request already in flight when the probe latched will still 401. Letting
          // currentSession() run here would trigger Amplify's cleanUpInvalidSession, whose
          // Hub signOut commits LOGOUT, clears sessionExpired and tears the modal down.
          if (
            error.response?.status === 401 &&
            originalRequest &&
            !originalRequest._retry &&
            !Store.state.sessionExpired
          ) {
            originalRequest._retry = true;
            try {
              const session = await Auth.currentSession();
              const idToken = session.getIdToken().getJwtToken();
              originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${idToken}` };
              return this.axiosInstance(originalRequest);
            } catch {
              // The probe in getAuthHeaders normally raises the modal before a request is
              // ever sent; reaching here means the failure was not attributable to expiry,
              // so fall back to login rather than stranding the user with neither.
              await useSessionExpiry().redirectToLogin();
            }
          }
          return Promise.reject(error);
        },
      );
    }
  }

  protected get = async <T, R = AxiosResponse<T>>(
    url: string,
    queryParams?: QueryParams,
    config?: AxiosRequestConfig,
  ): Promise<R> => {
    const authHeaders = await getAuthHeaders();
    const clientViewState = Store.state.clientViewState;
    const clientViewHeaders = {} as Record<string, string>;

    if (clientViewState.isClientViewEnabled) {
      clientViewHeaders['X-Client-View-Enabled'] = '1';

      const clientViewId = clientViewState.activeView?.id;
      if (clientViewId && clientViewId != -1) {
        clientViewHeaders['X-Client-View-ID'] = clientViewId.toString();
      }
    }

    if (queryParams) {
      const stringified = convertToQueryString(queryParams);
      return this.axiosInstance.get<T, R>(`${url}?${stringified}`, {
        ...config,
        headers: { ...config?.headers, ...authHeaders, ...clientViewHeaders },
      });
    } else {
      return this.axiosInstance.get<T, R>(url, {
        ...config,
        headers: { ...config?.headers, ...authHeaders, ...clientViewHeaders },
      });
    }
  };

  protected post = async <T, R = AxiosResponse<T>, P = unknown>(url: string, payload: P): Promise<R> => {
    const authHeaders = await getAuthHeaders();
    return this.axiosInstance.post<T, R>(url, payload, { headers: authHeaders }).catch((error) => {
      return error;
    });
  };

  protected postFile = async <T, R = AxiosResponse<T>>(
    url: string,
    payload: File,
    fields?: Record<string, string>,
  ): Promise<R> => {
    const authHeaders = await getAuthHeaders();
    const formData = new FormData();
    formData.append('file', payload);
    for (const [name, value] of Object.entries(fields ?? {})) {
      formData.append(name, value);
    }
    return this.axiosInstance.post<T, R>(url, formData, { headers: authHeaders }).catch((error) => error);
  };

  protected put = async <T, R = AxiosResponse<T>, P = unknown>(url: string, payload: P): Promise<R> => {
    const authHeaders = await getAuthHeaders();
    return this.axiosInstance.put<T, R>(url, payload, { headers: authHeaders });
  };

  protected patch = async <T = never, R = AxiosResponse<T>, P = unknown>(url: string, payload: P): Promise<R> => {
    const authHeaders = await getAuthHeaders();
    return this.axiosInstance.patch<T, R>(url, payload, { headers: authHeaders }).catch((error) => error);
  };

  protected delete = async <T, R = AxiosResponse<T>>(url: string): Promise<R> => {
    const authHeaders = await getAuthHeaders();
    return this.axiosInstance.delete<T, R>(url, { headers: authHeaders });
  };

  protected handleResponse = <R>(res: AxiosResponse<R> | AxiosError<R>): Promise<R> => {
    if (isAxiosError(res)) {
      const status = res.response?.status;
      // Prefer the backend's own message ({ error } or { message }) over statusText, which is
      // just "Bad Request" and told the user nothing about why their request was rejected.
      // handleTableauResponse has read `error` this way for a while; this is the same rule
      // applied to every endpoint.
      const body = res.response?.data as { error?: unknown; message?: unknown } | undefined;
      // Only strings are usable as an Error message; a nested object (e.g. { error: { code,
      // detail } }) must not fall through to `new Error(object)`, which stringifies to the
      // useless "[object Object]" and also breaks callers that match on the message text.
      const asString = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
      const message =
        asString(body?.error) ||
        asString(body?.message) ||
        res.response?.statusText ||
        res.message ||
        (status ? `HTTP ${status}` : 'Request failed');
      throw new Error(message, { cause: res.response });
    } else if (res.status > 299) {
      throw new Error(res.statusText || `HTTP ${res.status}`);
    }
    return Promise.resolve(res.data);
  };

  protected handleTableauResponse = <R>(res: AxiosResponse<R> | AxiosError<R>): Promise<R> => {
    if (isAxiosError(res)) {
      // Surface the backend-provided message (e.g. { error: 'human-readable error message' })
      const backendError = (res.response?.data as { error?: string } | undefined)?.error;
      if (backendError) {
        throw new Error(backendError, { cause: res.response });
      }
    } else if (res.headers['x-tableau-user-id']) {
      sessionStorage.setItem('tableauUserId', res.headers['x-tableau-user-id'] as string);
    }
    return this.handleResponse(res);
  };

  protected handleCreateResponse = <R>(res: AxiosResponse<R> | AxiosError<R>): Promise<R> => {
    if (isAxiosError(res)) {
      throw new Error(res.response?.statusText, { cause: res.response?.data });
    } else {
      if (res.status > 299) {
        throw new Error(res.statusText);
      }
      return Promise.resolve(res.data);
    }
  };

  protected handleInsertResponse = <R>(res: AxiosResponse<R>): Promise<R> => {
    if (res.status > 299) {
      throw new Error(res.statusText);
    }
    return Promise.resolve(res.headers.location);
  };

  protected handleFile = (res: AxiosResponse): Promise<string> => {
    if (res.status > 299) {
      throw new Error(res.statusText);
    }
    const { data, headers } = res;
    const blob = new Blob([data], { type: headers['content-type'] });
    const url = URL.createObjectURL(blob);
    return Promise.resolve(url);
  };
}

class CarrierService extends Service {
  buildTopPath(
    startDate: DateTime,
    endDate: DateTime,
    limit: number,
    coverageTypes: string[],
    carrierGroups: string[],
  ): string {
    let path = `?startDate=${startDate.toISODate()}`;
    path += `&endDate=${endDate.toISODate()}`;
    path += `&limit=${limit}`;
    path += `&${getArrayQueryString('coverageType', coverageTypes)}`;
    path += `&${getArrayQueryString('carrierGroup', carrierGroups)}`;
    return path;
  }

  async findTopBillable(
    startDate: DateTime,
    endDate: DateTime,
    defaultLimit: number,
    coverageType: string[],
    carrierGroup: string[],
  ): Promise<CarrierLabelValue[]> {
    const limit = carrierGroup.length > defaultLimit && defaultLimit != 0 ? carrierGroup.length : defaultLimit;
    const dateRange = { startDate: startDate.toISODate(), endDate: endDate.toISODate() };
    return (await this.get('/v1/charts/carrier/top-billable', { ...dateRange, limit, coverageType, carrierGroup }).then(
      this.handleResponse,
    )) as CarrierLabelValue[];
  }

  async findCommissions(startDate: DateTime, endDate: DateTime): Promise<CommissionValue[]> {
    const dateRange = { startDate: startDate.toISODate(), endDate: endDate.toISODate() };
    return (await this.get('/v1/commission', { ...dateRange }).then(this.handleResponse)) as CommissionValue[];
  }

  async findTopCommission(
    startDate: DateTime,
    endDate: DateTime,
    defaultLimit: number,
    coverageTypes: string[],
    carrierGroups: string[],
  ): Promise<LabelValue[]> {
    const limit = carrierGroups.length > defaultLimit ? carrierGroups.length : defaultLimit;
    const url = `/v1/charts/carrier/top-commission${this.buildTopPath(startDate, endDate, limit, coverageTypes, carrierGroups)}`;
    return (await this.get(url).then(this.handleResponse)) as LabelValue[];
  }

  async list(withPoliciesOnly = false): Promise<Carrier[]> {
    const queryParams = withPoliciesOnly ? { withPoliciesOnly } : undefined;
    return (await this.get('/v1/carrier', queryParams).then(this.handleResponse)) as Carrier[];
  }

  async findById(id: string | number): Promise<Carrier> {
    return (await this.get(`/v1/carrier/${id}`).then(this.handleResponse)) as Carrier;
  }

  async policiesById(id: string | number, params: CarrierPoliciesByIdParams): Promise<EpicPolicy[]> {
    return await this.get<EpicPolicy[]>(`/v1/carrier/${id}/policies`, { ...params }).then(this.handleResponse);
  }

  async premiumTotalByMonth(
    id: string | number,
    agencyTypes: string[],
    coverageTypes: string[],
    departments: string[],
    carrierIds: number[],
    profitCenters: number[],
  ): Promise<MultiSeries> {
    return (await this.get(`/v1/carrier/${id}/premium-total-by-month`, {
      agencyTypes,
      coverageTypes,
      departments,
      carrierIds,
      profitCenters,
    }).then(this.handleResponse)) as MultiSeries;
  }

  async premiumTotalVSOthers(
    id: string | number,
    carrierName: string,
    limit: number,
    agencyTypes: string[],
    coverageTypes: string[],
    departments: string[],
    carrierIds: number[],
    profitCenters: number[],
  ): Promise<MultiSeries> {
    type resultType = {
      insurerGroup: string;
      agencyName: string;
      sourceAgencyKey: string;
      value: number;
      highlight: boolean;
    };
    const result = (await this.get(`/v1/carrier/${id}/premium-vs-others`, {
      agencyTypes,
      coverageTypes,
      departments,
      carrierIds,
      profitCenters,
    }).then(this.handleResponse)) as resultType[];

    const insurerGroupTotal = result.reduce((acc, r) => {
      acc.set(r.insurerGroup, (acc.get(r.insurerGroup) || 0) + r.value);
      return acc;
    }, new Map<string, number>());

    const sortedInsurerGroups = Array.from(insurerGroupTotal.entries()).sort((e1, e2) => e2[1] - e1[1]);

    const insurerGroups = sortedInsurerGroups.slice(0, limit);

    // make sure carrier is always visible
    if (!insurerGroups.find((e) => e[0] === carrierName)) {
      insurerGroups.pop();
      insurerGroups.push(sortedInsurerGroups.find((e) => e[0] === carrierName) || [carrierName, 0]);
      insurerGroups.sort((e1, e2) => e2[1] - e1[1]);
    }

    const agencies = new Map<string, { name: string; key: string }>();
    result.forEach((r) => agencies.set(r.agencyName, { name: r.agencyName, key: r.sourceAgencyKey }));

    const categories = [{ category: insurerGroups.map((insurerGroup) => ({ label: insurerGroup[0] })) }];
    const valueOf = (insurerGroup: string, agencyName: string): { value?: number; color?: string } => {
      const data = result.find((r) => r.insurerGroup === insurerGroup && r.agencyName === agencyName);
      if (data) {
        const color = data.highlight ? undefined : chartMutedColor;
        return { value: data.value > 0 ? data.value : undefined, color };
      }
      return { value: undefined };
    };

    const sortedAgencies = Array.from(agencies.values()).sort((a, b) =>
      agencyLegendOrder.indexOf(a.key) > agencyLegendOrder.indexOf(b.key) ? 1 : -1,
    );
    const dataset = sortedAgencies.map((agency) => ({
      seriesname: agency.name,
      data: insurerGroups.flatMap((insurerGroup) => valueOf(insurerGroup[0], agency.name)),
      color: colorsBySourceAgencyKey.get(agency.key),
    }));
    return { categories, dataset };
  }

  async premiumTotalByCoverageType(
    id: string | number,
    agencyTypes: string[],
    coverageTypes: string[],
    departments: string[],
    carrierIds: number[],
    profitCenters: number[],
    limit = 15,
  ): Promise<MultiSeries> {
    const result = (await this.get(`/v1/carrier/${id}/premium-total-by-coverage-type`, {
      agencyTypes,
      coverageTypes,
      departments,
      carrierIds,
      profitCenters,
    }).then(this.handleResponse)) as {
      coverageType: string;
      agencyName: string;
      sourceAgencyKey: string;
      premium: number;
    }[];

    const coverageTypesWithPremium = Array.from(
      result.reduce((coverageTypeToPremiumMap, r) => {
        coverageTypeToPremiumMap.set(r.coverageType, (coverageTypeToPremiumMap.get(r.coverageType) || 0) + r.premium);
        return coverageTypeToPremiumMap;
      }, new Map<string, number>()),
    ).sort();

    // sort coverage types by premium desc, and limit results
    const sortedCoverages = Array.from(coverageTypesWithPremium.values())
      .sort((a: [string, number], b: [string, number]): number => b[1] - a[1])
      .slice(0, limit);
    const agencies = new Map<string, { name: string; key: string }>();
    result.forEach((r) => agencies.set(r.agencyName, { name: r.agencyName, key: r.sourceAgencyKey }));

    const sortedAgencies = Array.from(agencies.values()).sort((a, b) =>
      agencyLegendOrder.indexOf(a.key) > agencyLegendOrder.indexOf(b.key) ? 1 : -1,
    );
    const dataset = sortedAgencies.map((agency) => ({
      seriesname: agency.name,
      data: sortedCoverages.map((c) => ({
        value: result
          .filter((r) => r.coverageType === c[0] && r.agencyName === agency.name)
          .reduce((totalPremium, d) => totalPremium + d.premium, 0),
      })),
      color: colorsBySourceAgencyKey.get(agency.key),
    }));

    return {
      categories: [{ category: sortedCoverages.map((t) => ({ label: t[0] })) as MultiSeriesLabel[] }],
      dataset: dataset as MultiSeriesDataset[],
    };
  }

  async premiumTotalByAgency(
    id: string | number,
    coverageTypes: string[],
    departments: string[],
    carrierIds: number[],
    agencyTypes: string[],
    profitCenters: number[],
  ): Promise<MultiSeries> {
    const data = (await this.get(`/v1/carrier/${id}/premium-by-agency`, {
      coverageTypes,
      departments,
      carrierIds,
      agencyTypes,
      profitCenters,
    }).then(this.handleResponse)) as CarrierPremiumByAgency[];
    const years = Array.from(data.reduce((acc, d) => acc.add(d.policyYear), new Set())).sort();

    const companies = new Map<string, { name: string; key: string }>();
    data.forEach((d) => companies.set(d.agencyName, { name: d.agencyName, key: d.sourceAgencyKey }));

    const sortedCompanies = Array.from(companies.values()).sort((a, b) =>
      agencyLegendOrder.indexOf(a.key) > agencyLegendOrder.indexOf(b.key) ? 1 : -1,
    );
    const dataset = sortedCompanies.map((c) => ({
      seriesname: c.name,
      data: years.map((y) => ({
        value: data
          .filter((d) => d.policyYear === y && d.agencyName === c.name)
          .reduce((acc, d) => acc + d.policyPremium, 0),
      })),
      color: colorsBySourceAgencyKey.get(c.key),
    }));
    return {
      categories: [
        {
          category: years.map((y) => ({ label: `${y} YTD` })) as MultiSeriesLabel[],
        },
      ],
      dataset: dataset as MultiSeriesDataset[],
    };
  }

  async premiumByWholesaler(
    id: string | number,
    agencyTypes: string[],
    coverageTypes: string[],
    departments: string[],
    carrierIds: number[],
    profitCenters: number[],
  ): Promise<CarrierPremiumByWholesaler[]> {
    return (await this.get(`/v1/carrier/${id}/premium-by-wholesaler-group`, {
      agencyTypes,
      coverageTypes,
      departments,
      carrierIds,
      profitCenters,
    }).then(this.handleResponse)) as CarrierPremiumByWholesaler[];
  }

  async kpi(
    id: string | number,
    agencyTypes: string[],
    coverageTypes: string[],
    departments: string[],
    carrierIds: number[],
    profitCenters: number[],
  ): Promise<CarrierKPI> {
    return (await this.get(`/v1/carrier/${id}/kpi`, {
      agencyTypes,
      coverageTypes,
      departments,
      carrierIds,
      profitCenters,
    }).then(this.handleResponse)) as CarrierKPI;
  }

  async carrierTopClients(
    id: string | number,
    agencyTypes: string[],
    coverageTypes: string[],
    departments: string[],
    carrierIds: number[],
    profitCenters: number[],
  ): Promise<CarrierTopClients[]> {
    return (await this.get(`/v1/carrier/${id}/top-clients`, {
      agencyTypes,
      coverageTypes,
      departments,
      carrierIds,
      profitCenters,
    }).then(this.handleResponse)) as CarrierTopClients[];
  }

  async financialsById(id: string | number): Promise<CarrierFinancials[]> {
    return (await this.get(`/v1/carrier-financials?id=${id}`).then(this.handleResponse)) as CarrierFinancials[];
  }

  async financialsByName(name: string): Promise<CarrierFinancials[]> {
    return (await this.get(`/v1/carrier-financials?name=${name}`).then(this.handleResponse)) as CarrierFinancials[];
  }

  async carrierSurveyYears(coverageType: string): Promise<number[]> {
    return await this.get<number[]>(`/v2/carrier-survey/${coverageType}/years`).then(this.handleResponse);
  }

  async carrierSurvey(
    coverageType: string,
    year: number[],
    industry: number[],
    responderFocus: number[],
  ): Promise<CarrierSurveyResult[]> {
    return await this.get<CarrierSurveyResult[]>(`/v2/carrier-survey/${coverageType}`, {
      year,
      industry,
      responderFocus,
    }).then(this.handleResponse);
  }

  async surveyResultsByYear(
    coverageType: string,
    year: number[],
    industry: number[],
    responderFocus: number[],
  ): Promise<CarrierSurveyResult[]> {
    return await this.get<CarrierSurveyResult[]>(`/v2/carrier-survey/results-by-year/${coverageType}`, {
      year,
      industry,
      responderFocus,
    }).then(this.handleResponse);
  }

  async amBestRatings(): Promise<CarrierGroup[]> {
    return (await this.get('/v1/carrier-ratings').then(this.handleResponse)) as CarrierGroup[];
  }

  async uploadAMBestRatings(file: File): Promise<void> {
    await this.postFile('/v1/admin/carrier/ambest/companies', file).then(this.handleResponse);
  }

  async issuingCompanies(carrierId: string | number): Promise<IssuingCompany[]> {
    return (await this.get(`/v1/carrier/${carrierId}/issuing-companies`).then(this.handleResponse)) as IssuingCompany[];
  }

  async shareOfWallet(
    insurerGroupId: number,
    carrierIds: number[],
    agencyTypes?: string[],
    coverageTypes?: string[],
    departments?: string[],
  ): Promise<CarrierShareOfWallet[]> {
    return (await this.get(`/v1/carrier/${insurerGroupId}/share-of-wallet`, {
      agencyTypes,
      coverageTypes,
      departments,
      carrierIds,
    }).then(this.handleResponse)) as CarrierShareOfWallet[];
  }
}

class ReferenceService extends Service {
  async coverageTypes(): Promise<LabelValue[]> {
    return (await this.get('/v1/reference/coverage-type').then(this.handleResponse)) as LabelValue[];
  }

  async agencyTypes(): Promise<Agency[]> {
    return (await this.get('/v1/reference/agency-types').then(this.handleResponse)) as Agency[];
  }

  async carrierGroups(): Promise<LabelValue[]> {
    return (await this.get('/v1/reference/carrier-group').then(this.handleResponse)) as LabelValue[];
  }

  async carrierGroupsAll(): Promise<LabelValue[]> {
    return (await this.get('/v1/reference/carrier-group?all=true').then(this.handleResponse)) as LabelValue[];
  }

  // The carrier, coverage type and wholesaler values an external policy can be created
  // with. Drawn server-side from the tables the create validates against, so anything
  // offered here is something the create will accept.
  async externalPolicyEntities(): Promise<ExternalPolicyEntityOptions> {
    return (await this.get('/v1/reference/external-policy-entities').then(
      this.handleResponse,
    )) as ExternalPolicyEntityOptions;
  }

  async topLevelIndustries(): Promise<TopLevelClientIndustry[]> {
    return (await this.get('/v1/reference/top-level-client-industries').then(
      this.handleResponse,
    )) as TopLevelClientIndustry[];
  }

  async stockExchanges(): Promise<StockExchange[]> {
    return (await this.get('/v1/reference/stock-exchanges').then(this.handleResponse)) as StockExchange[];
  }

  async departments(): Promise<LabelValue<string>[]> {
    return await this.get<LabelValue<string>[]>('/v1/reference/departments').then(this.handleResponse);
  }

  async exposureTypes(): Promise<ExposureMetric[]> {
    return await this.get<ExposureMetric[]>('/v1/reference/exposure-metric').then(this.handleResponse);
  }

  async exposureCategories(): Promise<ExposureLabelValue[]> {
    return await this.get<ExposureLabelValue[]>('/v1/reference/exposure-category').then(this.handleResponse);
  }

  async exposureOptions(lookupKey: string): Promise<ExposureOption[]> {
    return await this.get<ExposureOption[]>('/v1/reference/exposure-options', { lookupKey }).then(this.handleResponse);
  }

  async quoteResponseTypes(): Promise<QuoteRefDataType[]> {
    return await this.get<QuoteRefDataType[]>('/v1/reference/quote-response-types').then(this.handleResponse);
  }

  async quoteDeclineReasonTypes(): Promise<QuoteRefDataType[]> {
    return await this.get<QuoteRefDataType[]>('/v1/reference/quote-decline-reason-types').then(this.handleResponse);
  }

  async quoteItemTypesByCoverageType(coverageTypeId: number): Promise<QuoteItemType[]> {
    return await this.get<QuoteItemType[]>('/v1/reference/quote-item-types', {
      lookupType: 'coverage-type',
      lookupValue: coverageTypeId,
    }).then(this.handleResponse);
  }

  async exposurePropertyRefData(): Promise<ExposurePropertyRefData[]> {
    return await this.get<ExposurePropertyRefData[]>('/v1/reference/exposure-property').then(this.handleResponse);
  }

  async syndicates(): Promise<Syndicate[]> {
    return await this.get<Syndicate[]>('/v1/reference/syndicates').then(this.handleResponse);
  }

  async accountAttributeTypes(): Promise<AccountAttribute[]> {
    return await this.get<AccountAttribute[]>('/v1/reference/account-attribute-types').then(this.handleResponse);
  }

  async industries(): Promise<Industry[]> {
    return await this.get<Industry[]>('/v1/reference/industries').then(this.handleResponse);
  }

  async surveyResponseIndustries(): Promise<IdValue<string>[]> {
    return await this.get<IdValue<string>[]>('/v2/carrier-survey/response-industries').then(this.handleResponse);
  }

  async surveyResponderFocuses(): Promise<{ responderFocus: string; id: number }[]> {
    return await this.get<{ responderFocus: string; id: number }[]>('/v1/reference/survey-responder-focus').then(
      this.handleResponse,
    );
  }

  async sicCodes(): Promise<ClientIndustry[]> {
    return await this.get<ClientIndustry[]>('/v1/reference/client-industries').then(this.handleResponse);
  }

  async profitCenters(): Promise<ProfitCenter[]> {
    return await this.get<ProfitCenter[]>('/v1/reference/profit-center').then(this.handleResponse);
  }

  async stockDropFactors(): Promise<StockDropFactor[]> {
    return await this.get<StockDropFactor[]>('/v1/reference/stock-drop-factors').then(this.handleResponse);
  }

  async products(): Promise<Product[]> {
    return await this.get<Product[]>('/v1/reference/products').then(this.handleResponse);
  }
}

class UniversalSearchService extends Service {
  async search(params: UniversalSearchParams): Promise<UniversalSearchResult[]> {
    return (await this.get<UniversalSearchResult[]>('/v1/universal-search', { ...params }).then(
      this.handleResponse,
    )) as UniversalSearchResult[];
  }
}

class WholesalerGroupService extends Service {
  async list(showAll = false): Promise<WholesalerGroup[]> {
    const showAllParam = showAll ? { all: true } : undefined;
    return (await this.get('/v1/wholesaler-group', showAllParam).then(this.handleResponse)) as WholesalerGroup[];
  }

  async findById(id: number | string): Promise<WholesalerGroup> {
    return (await this.get(`/v1/wholesaler-group/${id}`).then(this.handleResponse)) as WholesalerGroup;
  }

  async kpi(id: number | string, wholesalerIds?: number[], agencyTypes?: string[]): Promise<WholesalerGroupKPI> {
    return (await this.get(`/v1/wholesaler-group/${id}/kpi`, { wholesalers: wholesalerIds, agencyTypes }).then(
      this.handleResponse,
    )) as WholesalerGroupKPI;
  }

  async premiumTotalByMonth(
    id: string | number,
    wholesalerIds?: number[],
    agencyTypes?: string[],
    profitCenters?: number[],
  ): Promise<MultiSeries> {
    return (await this.get(`/v1/wholesaler-group/${id}/premium-total-by-month`, {
      wholesalers: wholesalerIds,
      agencyTypes,
      profitCenters,
    }).then(this.handleResponse)) as MultiSeries;
  }

  async premiumTotalVSOthers(
    id: string | number,
    wholesalerIds?: number[],
    agencyTypes?: string[],
    profitCenters?: number[],
  ): Promise<MultiSeries> {
    return this._premiumTotalVSOthers(
      `/v1/wholesaler-group/${id}/premium-vs-others`,
      wholesalerIds,
      agencyTypes,
      profitCenters,
    );
  }

  async customGroupPremiumTotalVSOthers(
    id: string | number,
    wholesalerIds?: number[],
    agencyTypes?: string[],
    profitCenters?: number[],
  ): Promise<MultiSeries> {
    return this._premiumTotalVSOthers(
      `/v1/wholesaler-group/custom-selection/${id}/premium-vs-others`,
      wholesalerIds,
      agencyTypes,
      profitCenters,
    );
  }

  private async _premiumTotalVSOthers(
    url: string,
    wholesalerIds?: number[],
    agencyTypes?: string[],
    profitCenters?: number[],
  ): Promise<MultiSeries> {
    type wholesalerStat = {
      label: string;
      value: number;
      agencyName: string;
      sourceAgencyKey: string;
      highlight: boolean;
    };
    const result = (await this.get(url, { wholesalers: wholesalerIds, agencyTypes, profitCenters }).then(
      this.handleResponse,
    )) as wholesalerStat[];

    const wholesalerMap = result.reduce((wgToPremiumTotalMap, r) => {
      if (!wgToPremiumTotalMap.has(r.label)) wgToPremiumTotalMap.set(r.label, []);
      const stat = wgToPremiumTotalMap.get(r.label);
      if (stat) stat.push(r);

      return wgToPremiumTotalMap;
    }, new Map<string, wholesalerStat[]>());

    const sumPremiums = (total: number, w: wholesalerStat): number => total + w.value;
    const entries = Array.from(wholesalerMap.entries()).sort(
      (w1, w2) => w2[1].reduce(sumPremiums, 0) - w1[1].reduce(sumPremiums, 0),
    );

    const agencies = new Map<string, { name: string; key: string }>();
    result.forEach((r) => agencies.set(r.agencyName, { name: r.agencyName, key: r.sourceAgencyKey }));

    const sortedAgencies = Array.from(agencies.values()).sort((a, b) =>
      agencyLegendOrder.indexOf(a.key) > agencyLegendOrder.indexOf(b.key) ? 1 : -1,
    );

    const dataset = sortedAgencies.map((agency) => ({
      seriesname: agency.name,
      data: entries.map((e) => {
        const f = e[1].find((ws) => ws.agencyName === agency.name);
        const color = f?.highlight ? undefined : chartMutedColor;
        return { value: f?.value || null, color };
      }),
      color: colorsBySourceAgencyKey.get(agency.key),
    }));

    return {
      categories: [{ category: entries.map((e) => ({ label: e[0] })) as MultiSeriesLabel[] }],
      dataset: dataset as MultiSeriesDataset[],
    };
  }

  async premiumTotalByCoverageType(
    id: string | number,
    wholesalerIds: number[],
    agencyTypes: string[],
    profitCenters: number[],
    limit = 15,
  ): Promise<MultiSeries> {
    return this._premiumTotalByCoverageType(
      `/v1/wholesaler-group/${id}/premium-total-by-coverage-type`,
      wholesalerIds,
      agencyTypes,
      profitCenters,
      limit,
    );
  }

  async customGroupPremiumTotalByCoverageType(
    id: string | number,
    wholesalerIds: number[],
    agencyTypes: string[],
    profitCenters: number[],
    limit = 15,
  ): Promise<MultiSeries> {
    return this._premiumTotalByCoverageType(
      `/v1/wholesaler-group/custom-selection/${id}/premium-total-by-coverage-type`,
      wholesalerIds,
      agencyTypes,
      profitCenters,
      limit,
    );
  }

  private async _premiumTotalByCoverageType(
    url: string,
    wholesalerIds: number[],
    agencyTypes: string[],
    profitCenters: number[],
    limit = 15,
  ): Promise<MultiSeries> {
    const result = (await this.get(url, { wholesalers: wholesalerIds, agencyTypes, profitCenters }).then(
      this.handleResponse,
    )) as { coverageType: string; agencyName: string; sourceAgencyKey: string; premium: number }[];

    const coverageTypes = Array.from(
      result.reduce((coverageTypeToPremiumMap, r) => {
        coverageTypeToPremiumMap.set(r.coverageType, (coverageTypeToPremiumMap.get(r.coverageType) || 0) + r.premium);
        return coverageTypeToPremiumMap;
      }, new Map<string, number>()),
    ).sort();

    // sort coverage types by premium desc, and limit results
    const sortedCoverages = Array.from(coverageTypes.values())
      .sort((a: [string, number], b: [string, number]): number => b[1] - a[1])
      .slice(0, limit);
    const agencies = new Map<string, { name: string; key: string }>();
    result.forEach((r) => agencies.set(r.agencyName, { name: r.agencyName, key: r.sourceAgencyKey }));

    const sortedAgencies = Array.from(agencies.values()).sort((a, b) =>
      agencyLegendOrder.indexOf(a.key) > agencyLegendOrder.indexOf(b.key) ? 1 : -1,
    );
    const dataset = sortedAgencies.map((agency) => ({
      seriesname: agency.name,
      data: sortedCoverages.map((c) => ({
        value: result
          .filter((r) => r.coverageType === c[0] && r.agencyName === agency.name)
          .reduce((totalPremium, d) => totalPremium + d.premium, 0),
      })),
      color: colorsBySourceAgencyKey.get(agency.key),
    }));

    return {
      categories: [{ category: sortedCoverages.map((t) => ({ label: t[0] })) as MultiSeriesLabel[] }],
      dataset: dataset as MultiSeriesDataset[],
    };
  }

  async premiumByCarrier(
    wholesalerGroupName: string,
    id: string | number,
    wholesalerIds?: number[],
    agencyTypes?: string[],
    profitCenters?: number[],
  ): Promise<Sankey> {
    return this._premiumByCarrier(
      `/v1/wholesaler-group/${id}/premium-by-carrier`,
      wholesalerGroupName,
      wholesalerIds,
      agencyTypes,
      profitCenters,
    );
  }

  async customGroupPremiumByCarrier(
    wholesalerGroupName: string,
    id: string | number,
    wholesalerIds?: number[],
    agencyTypes?: string[],
    profitCenters?: number[],
  ): Promise<Sankey> {
    return this._premiumByCarrier(
      `/v1/wholesaler-group/custom-selection/${id}/premium-by-carrier`,
      wholesalerGroupName,
      wholesalerIds,
      agencyTypes,
      profitCenters,
    );
  }

  private async _premiumByCarrier(
    url: string,
    wholesalerGroupName: string,
    wholesalerIds?: number[],
    agencyTypes?: string[],
    profitCenters?: number[],
  ): Promise<Sankey> {
    const data = (await this.get(url, { wholesalers: wholesalerIds, agencyTypes, profitCenters }).then(
      this.handleResponse,
    )) as WholesalerPremiumByCarrier[];

    // if no data, just exit
    if (!data || data.length === 0) {
      return { nodes: [], links: [] } as Sankey;
    }

    const ALL_OTHER_COVERAGE_TYPES = 'All Other Coverage Types';
    const ALL_OTHER_CARRIERS = 'All Other Carriers';

    // get list of carriers while totaling their premiums by coverage type
    const carrierMap = data.reduce((acc, d) => {
      const key = d.carrier || 'Unknown';
      acc.set(key, (acc.get(key) || 0) + d.policyPremium);
      return acc;
    }, new Map<string, number>());

    const carrierSankeyDataNodes: Array<SankeyDataNode> = transformMapToShowTop(20, carrierMap);
    const carrierSankeyViewNodes: Array<SankeyNode> = createSankeyViewNodes(carrierSankeyDataNodes, ALL_OTHER_CARRIERS);

    // get list of coverage types while totaling premiums by carrier
    const coverageTypeMap = data.reduce((acc, d) => {
      const key = d.coverageType || 'N/A';
      acc.set(key, (acc.get(key) || 0) + d.policyPremium);
      return acc;
    }, new Map<string, number>());

    const coverageTypeSankeyDataNodes: Array<SankeyDataNode> = transformMapToShowTop(10, coverageTypeMap);
    const coverageTypeSankeyViewNodes = createSankeyViewNodes(coverageTypeSankeyDataNodes, ALL_OTHER_COVERAGE_TYPES);

    // create Coverage Type links for the ungrouped nodes
    let coverageTypeLinks = coverageTypeSankeyDataNodes
      .filter((n) => !n.grouped)
      .map((c) => {
        return {
          from: wholesalerGroupName,
          to: wholesalerGroupName === c.label ? `Direct ${c.label}` : c.label,
          value: coverageTypeMap.get(c.label),
        };
      }) as SankeyLink[];

    // WholesalerGroup <--> Coverage Type Links
    coverageTypeLinks = [
      ...coverageTypeLinks,
      {
        from: wholesalerGroupName,
        to: ALL_OTHER_COVERAGE_TYPES,
        value: coverageTypeSankeyDataNodes
          .filter((n) => n.grouped)
          .reduce((total, node) => total + node.aggregateTotal, 0),
      },
    ];

    // Coverage Type <--> Carrier Links
    let carrierLinks = carrierSankeyDataNodes.flatMap((c) => {
      return coverageTypeSankeyDataNodes.map((ct) => {
        return {
          from: ct.grouped ? ALL_OTHER_COVERAGE_TYPES : ct.label,
          to: (() => {
            if (ct.label === c.label) {
              return `Direct ${c.label}`;
            }
            if (c.grouped) {
              return ALL_OTHER_CARRIERS;
            }
            return c.label;
          })(),
          value: (
            data.find((d) => (d.carrier || 'Unknown') === c.label && (d.coverageType || 'N/A') === ct.label) || {
              policyPremium: 0,
            }
          ).policyPremium,
        };
      });
    }) as SankeyLink[];

    // consolidate the "All Other Coverage Types" <--> "All Other Carriers" links
    const filteredCarrierLinks = carrierLinks.filter(
      (l) => !(l.from === ALL_OTHER_COVERAGE_TYPES && l.to === ALL_OTHER_CARRIERS),
    );
    carrierLinks = [
      ...filteredCarrierLinks,
      {
        from: ALL_OTHER_COVERAGE_TYPES,
        to: ALL_OTHER_CARRIERS,
        value: carrierLinks.reduce(
          (total, l) => (l.from === ALL_OTHER_COVERAGE_TYPES && l.to === ALL_OTHER_CARRIERS ? total + l.value : total),
          0,
        ),
      },
    ];

    // Group the nodes and links and add chart metadata
    const carriers = carrierSankeyViewNodes.map((c, i) => ({
      label: c.label,
      showValue: true,
      color: paletteColors[i + 1],
    }));
    const coverageTypes = coverageTypeSankeyViewNodes.map((c, i) => ({
      label: c.label,
      showValue: true,
      color: paletteColors[i + 6],
    }));
    const nodes = carriers
      .concat(coverageTypes)
      .concat({ label: wholesalerGroupName, showValue: true, color: paletteColors[0] });
    const links = coverageTypeLinks.concat(carrierLinks);
    return { nodes, links } as Sankey;
  }

  async policiesById(id: string | number, params: WholesalerGroupPoliciesByIdParams): Promise<EpicPolicy[]> {
    return await this.get<EpicPolicy[]>(`/v1/wholesaler-group/${id}/policies`, { ...params }).then(this.handleResponse);
  }

  async policiesByCustomGroup(id: string | number, params: WholesalerGroupPoliciesByIdParams): Promise<EpicPolicy[]> {
    return await this.get<EpicPolicy[]>(`/v1/wholesaler-group/custom-selection/${id}/policies`, { ...params }).then(
      this.handleResponse,
    );
  }

  async wholesalers(id: string | number): Promise<Wholesaler[]> {
    return (await this.get(`/v1/wholesaler-group/${id}/wholesalers`).then(this.handleResponse)) as Wholesaler[];
  }

  async wholesalersByCustomGroup(id: string | number): Promise<Wholesaler[]> {
    return (await this.get(`/v1/wholesaler-group/custom-selection/${id}/wholesalers`).then(
      this.handleResponse,
    )) as Wholesaler[];
  }

  async premiumTotalByAgency(
    id: string | number,
    wholesalerIds: number[],
    agencyTypes: string[],
    profitCenters: number[],
  ): Promise<MultiSeries> {
    return this._premiumTotalByAgency(
      `/v1/wholesaler-group/${id}/premium-by-agency`,
      wholesalerIds,
      agencyTypes,
      profitCenters,
    );
  }

  async customGroupPremiumTotalByAgency(
    id: string | number,
    wholesalerIds: number[],
    agencyTypes: string[],
    profitCenters: number[],
  ): Promise<MultiSeries> {
    return this._premiumTotalByAgency(
      `/v1/wholesaler-group/custom-selection/${id}/premium-by-agency`,
      wholesalerIds,
      agencyTypes,
      profitCenters,
    );
  }

  private async _premiumTotalByAgency(
    url: string,
    wholesalerIds: number[],
    agencyTypes: string[],
    profitCenters: number[],
  ): Promise<MultiSeries> {
    const data = (await this.get(url, { wholesalers: wholesalerIds, agencyTypes, profitCenters }).then(
      this.handleResponse,
    )) as WholesalerPremiumByAgency[];

    const years = Array.from(data.reduce((acc, d) => acc.add(d.policyYear), new Set())).sort();
    const companies = new Map<string, { name: string; key: string }>();
    data.forEach((d) => companies.set(d.agencyName, { name: d.agencyName, key: d.sourceAgencyKey }));

    const dataset = Array.from(companies.values()).map((c) => ({
      seriesname: c.name,
      data: years.map((y) => ({
        value: data
          .filter((d) => d.policyYear === y && d.agencyName === c.name)
          .reduce((acc, d) => acc + d.policyPremium, 0),
      })),
      color: colorsBySourceAgencyKey.get(c.key),
    }));
    return {
      categories: [
        {
          category: years.map((y) => ({ label: `${y} YTD` })) as MultiSeriesLabel[],
        },
      ],
      dataset: dataset as MultiSeriesDataset[],
    };
  }

  async wholesalerGroupSelections(): Promise<WholesalerGroupSelection[]> {
    return (await this.get('/v1/wholesaler-group/custom-selection').then(this.handleResponse)) as Promise<
      WholesalerGroupSelection[]
    >;
  }

  async wholesalerPremiums(
    startDate: DateTime,
    endDate: DateTime,
    limit: number,
    coverageTypes: string[],
    wholesalerIds: number[],
  ): Promise<CategoryValueSeriesWithSourceAgencyKey[]> {
    const dateRange = { startDate: startDate.toISODate(), endDate: endDate.toISODate() };
    return (await this.get('/v1/wholesaler-group/premium-by-wholesaler', {
      ...dateRange,
      limit,
      coverageTypes,
      wholesalerIds,
    }).then(this.handleResponse)) as Promise<CategoryValueSeriesWithSourceAgencyKey[]>;
  }

  async createWholesalerGroupSelection(groupName: string, wholesalerGroupIds: number[]): Promise<void> {
    return (await this.post('/v1/wholesaler-group/custom-selection', { groupName, wholesalerGroupIds }).then(
      this.handleInsertResponse,
    )) as Promise<void>;
  }

  async deleteWholesalerGroupSelection(selectionId: number): Promise<void> {
    return (await this.delete(`/v1/wholesaler-group/custom-selection/${selectionId}`).then(
      this.handleResponse,
    )) as Promise<void>;
  }

  async customGroupById(selectionId: number | string): Promise<WholesalerGroup> {
    return (await this.get(`/v1/wholesaler-group/custom-selection/${selectionId}`).then(
      this.handleResponse,
    )) as Promise<WholesalerGroup>;
  }

  async customGroupKpi(
    id: number | string,
    wholesalerIds?: number[],
    agencyTypes?: string[],
    profitCenters?: number[],
  ): Promise<WholesalerGroupKPI> {
    return (await this.get(`/v1/wholesaler-group/custom-selection/${id}/kpi`, {
      wholesalers: wholesalerIds,
      agencyTypes,
      profitCenters,
    }).then(this.handleResponse)) as WholesalerGroupKPI;
  }

  async customGroupPremiumTotalByMonth(
    id: string | number,
    wholesalerIds?: number[],
    agencyTypes?: string[],
    profitCenters?: number[],
  ): Promise<MultiSeries> {
    return (await this.get(`/v1/wholesaler-group/custom-selection/${id}/premium-total-by-month`, {
      wholesalers: wholesalerIds,
      agencyTypes,
      profitCenters,
    }).then(this.handleResponse)) as MultiSeries;
  }
}

class PersonalService extends Service {
  async kpi(): Promise<PersonalKPI> {
    return this.get('/v1/personal/kpi').then(this.handleResponse) as Promise<PersonalKPI>;
  }
}

class VolatilityModelService extends Service {
  async list(): Promise<VolatilityModel[]> {
    return this.get('/v1/volatility-model').then(this.handleResponse) as Promise<VolatilityModel[]>;
  }

  async findById(id: number | string): Promise<VolatilityModel | undefined | null> {
    return this.get(`/v1/volatility-model/${id}`).then(this.handleResponse) as Promise<
      VolatilityModel | undefined | null
    >;
  }

  async findByAccountId(id: number | string): Promise<VolatilityModel[]> {
    return this.get(`/v1/volatility-model/account/${id}`).then(this.handleResponse) as Promise<VolatilityModel[]>;
  }

  async save(model: VolatilityModel): Promise<string> {
    return model.id ? this.update(model) : this.insert(model);
  }

  async insert(model: VolatilityModel): Promise<string> {
    return this.post<string>('/v1/volatility-model', model).then(this.handleInsertResponse);
  }

  async update(model: VolatilityModel): Promise<string> {
    await this.patch(`/v1/volatility-model/${model.id}`, model).then(this.handleResponse);
    return Promise.resolve('' + model.id);
  }

  async togglePinned(id: number): Promise<void> {
    return await this.patch(`/v1/volatility-model/${id}/toggle-pinned`, id).then(this.handleResponse);
  }

  async deleteVolatilityModel(id: number): Promise<void> {
    await this.delete(`/v1/volatility-model/${id}`).then(this.handleResponse);
  }
}

class StockDropModelService extends Service {
  async list(): Promise<StockDropModel[]> {
    return this.get('/v1/stock-drop-model').then(this.handleResponse) as Promise<StockDropModel[]>;
  }

  async listSettlementRanges(): Promise<StockDropSettlement[]> {
    return this.get<StockDropSettlement[]>('/v1/stock-drop-model/median-settlement').then(this.handleResponse);
  }

  async findById(id: number | string): Promise<StockDropModel | undefined | null> {
    return this.get(`/v1/stock-drop-model/${id}`).then(this.handleResponse) as Promise<
      StockDropModel | undefined | null
    >;
  }

  async findByAccountId(id: number | string): Promise<StockDropModel[]> {
    return this.get(`/v1/stock-drop-model/account/${id}`).then(this.handleResponse) as Promise<StockDropModel[]>;
  }

  async insert(model: any): Promise<string> {
    return this.post<string>('/v1/stock-drop-model', model).then(this.handleInsertResponse);
  }

  async update(model: any): Promise<string> {
    await this.patch(`/v1/stock-drop-model/${model.id}`, model).then(this.handleResponse);
    return Promise.resolve('' + model.id);
  }

  async togglePinned(id: number): Promise<string> {
    return await this.patch(`/v1/stock-drop-model/${id}/toggle-pinned`, id).then(this.handleResponse);
  }

  async deleteStockDropModel(id: number): Promise<void> {
    await this.delete(`/v1/stock-drop-model/${id}`).then(this.handleResponse);
  }
}

class LineStatusService extends Service {
  async list(): Promise<LineStatus[]> {
    return (await this.get('/v1/admin/line-status').then(this.handleResponse)) as LineStatus[];
  }

  async update(id: number, request: LineStatusUpdateRequest): Promise<void> {
    await this.put(`/v1/admin/line-status/${id}`, request);
  }

  async remove(id: number): Promise<void> {
    await this.delete(`/v1/admin/line-status/${id}`);
  }
}

class WholesalerService extends Service {
  async list(): Promise<WholesalerAdmin[]> {
    return (await this.get('/v1/wholesaler/list').then(this.handleResponse)) as WholesalerAdmin[];
  }

  async wholesalerGroups(): Promise<WholesalerGroup[]> {
    return (await this.get('/v1/admin/wholesalergroups').then(this.handleResponse)) as WholesalerGroup[];
  }

  async updateGroup(request: UpdateGroupRequest): Promise<void> {
    return await this.post('/v1/admin/wholesaler/updategroup', request);
  }

  async updateCountry(request: UpdateCountryRequest): Promise<void> {
    return await this.post('/v1/admin/wholesaler/updatecountry', request);
  }

  async updateWholesalerGroup(wholesalerGroupId: number, name: string, type: string | null | undefined): Promise<void> {
    return await this.post(`/v1/admin/wholesaler-group/${wholesalerGroupId}`, { name, type });
  }

  async deleteWholesalerGroup(wholesalerGroupId: number): Promise<void> {
    return await this.delete(`/v1/admin/wholesaler-group/${wholesalerGroupId}`);
  }
}

export interface AccountSearchParams {
  accountStatuses: string[];
  agencies: string;
  search: string;
  offset: number;
  limit: number;
  sortField: string;
  sortDirection: string;
  showCurrentOnly: boolean;
  revenueMin?: number;
  revenueMax?: number;
}

class AccountService extends Service {
  async list(): Promise<AccountSummary[]> {
    return ((await this.get('/v1/account/list').then(this.handleResponse)) as AccountSummary[]) ?? [];
  }

  async search(params: AccountSearchParams): Promise<AccountSummary[]> {
    return ((await this.get('/v1/account', { ...params }).then(this.handleResponse)) as AccountSummary[]) ?? [];
  }

  async uploadPolicies(id: string | number, policies: ExternalPolicy[]): Promise<ExternalPolicyUploadResponse> {
    return (await this.post(`/v1/account/${id}/externalpolicies`, policies).then(
      this.handleResponse,
    )) as ExternalPolicyUploadResponse;
  }

  async deletePolicy(accountId: string | number, policyId: string): Promise<void> {
    // Service.delete, unlike post and patch, rejects rather than resolving with the error, so
    // handleResponse has to be the rejection handler as well or the API's { error } never reaches
    // the caller.
    await this.delete(`/v1/account/${accountId}/externalpolicies/${policyId}`).then(
      this.handleResponse,
      this.handleResponse,
    );
  }

  async findById(id: string | number): Promise<Account> {
    return (await this.get(`/v1/account/${id}`).then(this.handleResponse)) as Account;
  }

  async attributes(id: string | number): Promise<number[]> {
    return (await this.get(`/v1/account/${id}/attributes`).then(this.handleResponse)) as number[];
  }

  async policiesById(
    id: string | number,
    expired: boolean,
    withoutProgram: boolean,
    showExternal: boolean,
    hideUnpublishedRoles: boolean = false,
    coverageType?: string[],
  ): Promise<EpicPolicy[]> {
    return (await this.get(`/v1/account/${id}/policies`, {
      expired,
      withoutProgram,
      showExternal,
      hideUnpublishedRoles,
      coverageType,
    }).then(this.handleResponse)) as EpicPolicy[];
  }

  async kpi(id: string | number): Promise<AccountKPI> {
    return (await this.get(`/v1/account/${id}/kpi`).then(this.handleResponse)) as AccountKPI;
  }

  async kpiByParentId(id: string | number, external: boolean, showCurrentOnly: boolean): Promise<AccountKPI> {
    return (await this.get(`/v1/parent/account/${id}/kpi`, { external, showCurrentOnly }).then(
      this.handleResponse,
    )) as AccountKPI;
  }

  async update(id: string | number, request: AccountPatchRequest): Promise<AccountUpdateResponse> {
    return await this.patch(`/v1/account/${id}/update`, request)
      .then(this.handleResponse)
      .then((data) => {
        if (data) {
          return data as AccountUpdateResponse;
        }

        return { epicFail: false } as AccountUpdateResponse;
      });
  }

  async premiumByCoverageType(
    id: number,
    internalOnly: boolean = false,
    showNonRecurring: boolean = false,
  ): Promise<LabelValue[]> {
    return (await this.get(`/v1/account/${id}/premium-by-coverage-type`, { internalOnly, showNonRecurring }).then(
      this.handleResponse,
    )) as LabelValue[];
  }

  async retentionByCoverageType(
    id: string | number,
    showExternal: boolean,
    showNonRecurring: boolean = false,
  ): Promise<LabelValue[]> {
    return (await this.get(`/v1/account/${id}/retention-by-coverage-type`, { showExternal, showNonRecurring }).then(
      this.handleResponse,
    )) as LabelValue[];
  }

  async maxLimitByCoverageType(
    id: string | number,
    showExternal: boolean,
    showNonRecurring: boolean = false,
  ): Promise<LabelValue[]> {
    return (await this.get(`/v1/account/${id}/max-limit-by-coverage-type`, { showExternal, showNonRecurring }).then(
      this.handleResponse,
    )) as LabelValue[];
  }

  async premiumByCarrier(
    id: number,
    internalOnly: boolean = false,
    showNonRecurring: boolean = false,
  ): Promise<LabelValue[]> {
    return (await this.get(`/v1/account/${id}/premium-by-carrier`, { internalOnly, showNonRecurring }).then(
      this.handleResponse,
    )) as LabelValue[];
  }

  async programExpiration(
    id: string | number,
    startDate: string,
    endDate: string,
    internalOnly: boolean = false,
    showNonRecurring: boolean = false,
  ): Promise<Program[]> {
    return (await this.get(`/v1/account/${id}/programs/expiring`, {
      startDate,
      endDate,
      internalOnly,
      showNonRecurring,
    }).then(this.handleResponse)) as Program[];
  }

  async getExposures(id: string | number): Promise<ExposureValue[]> {
    return await this.get<ExposureValue[]>(`/v1/account/${id}/exposures`).then(this.handleResponse);
  }

  async submitExposures(id: string | number, entries: ExposureValueEntry[]): Promise<void> {
    await this.post(`/v1/account/${id}/exposures`, entries).then(this.handleResponse);
  }

  async validateExposures(entries: ExposureValueEntry[]): Promise<string> {
    return (await this.post('/v1/validate/exposures', entries).then(this.handleResponse)) as string;
  }

  async updateExposures(id: string, entries: ExposureValueChangeEntry[]): Promise<void> {
    await this.put(`/v1/account/${id}/exposures`, entries).then(this.handleResponse);
  }

  async DeleteExposure(exposureId: string | number): Promise<void> {
    await this.delete(`/v1/exposure/${exposureId}`).then(this.handleResponse);
  }

  async GetClaimsForAccount(id: string | number): Promise<Claim[]> {
    return await this.get<Claim[]>(`/v1/account/${id}/claims`).then(this.handleResponse);
  }

  async exposureProperties(id: string | number, offset: number, limit: number): Promise<ExposureProperty[]> {
    return (await this.get(`/v1/account/${id}/exposures-property`, { offset, limit }).then(
      this.handleResponse,
    )) as ExposureProperty[];
  }

  async exposurePropertiesCount(id: string | number): Promise<number> {
    return this.get<number>(`/v1/account/${id}/exposures-property-count`).then(this.handleResponse);
  }

  async uploadExposureProperties(id: string | number, properties: ExposureProperty[]): Promise<string[]> {
    const handleError = (res: AxiosResponse<unknown>): Promise<string[]> => {
      if (isAxiosError(res)) {
        switch (res.response?.status) {
          case 400:
            return Promise.resolve(['The server was unable to process the uploaded file']);
          case 413:
            return Promise.resolve(['The uploaded file is too large']);
          default:
            if (res.response?.status && res.response.status > 299) {
              return Promise.resolve(['An error occurred while processing the uploaded file']);
            }
        }
      }
      return Promise.resolve([]);
    };

    return (await this.post(`/v1/account/${id}/exposures-property`, properties).then(handleError));
  }

  async deleteAllExposureProperty(accountId: string | number): Promise<void> {
    await this.delete(`/v1/account/${accountId}/exposures-property-all`);
  }

  async deleteExposureProperty(id: string | number, programID: string | number): Promise<void> {
    await this.delete(`/v1/account/${id}/exposures-property/${programID}`).then(this.handleResponse);
  }

  async getProgramsForAccount(
    id: string | number,
    expired: boolean,
    internalOnly: boolean = false,
    showNonRecurring: boolean = false,
  ): Promise<Program[]> {
    return await this.get<Program[]>(`/v1/account/${id}/programs`, { expired, internalOnly, showNonRecurring }).then(
      this.handleResponse,
    );
  }

  async getProgramDetails(
    id: string | number,
    programID: string | number,
    hideUnpublishedRoles: boolean = false,
  ): Promise<ProgramDetails> {
    return await this.get<ProgramDetails>(`/v1/account/${id}/programs/${programID}`, { hideUnpublishedRoles }).then(
      this.handleResponse,
    );
  }

  async createProgram(id: string | number, program: CreateProgramRequest): Promise<void> {
    await this.post(`/v1/account/${id}/programs`, program).then(this.handleResponse);
  }

  async updateProgram(id: string | number, programID: string | number, program: CreateProgramRequest): Promise<void> {
    await this.patch(`/v1/account/${id}/programs/${programID}`, program).then(this.handleResponse);
  }

  async deleteProgram(id: string | number, programID: string | number): Promise<void> {
    await this.delete(`/v1/account/${id}/programs/${programID}`).then(this.handleResponse);
  }

  async removePolicyFromProgram(
    accountId: string | number,
    programId: string | number,
    policyId: string | number,
  ): Promise<void> {
    await this.delete(`/v1/account/${accountId}/programs/${programId}/policy/${policyId}`).then(this.handleResponse);
  }

  async peersById(id: string | number): Promise<AccountPeer[]> {
    return (await this.get(`/v1/account/${id}/peers`).then(this.handleResponse)) as AccountPeer[];
  }

  async getProgramRates(id: string | number): Promise<ProgramRate[]> {
    return await this.get<ProgramRate[]>(`/v1/account/${id}/program-rates`).then(this.handleResponse);
  }

  async getActiveContacts(
    id: string | number,
    showNonRecurring: boolean = false,
    hideUnpublished: boolean = false,
  ): Promise<AccountContact[]> {
    return await this.get<AccountContact[]>(`/v1/account/${id}/active-contacts`, {
      showNonRecurring,
      hideUnpublished,
    }).then(this.handleResponse);
  }

  async getProgramSummary(
    id: string | number,
    internalOnly: boolean = false,
    showNonRecurring: boolean = false,
  ): Promise<ProgramSummary[]> {
    return await this.get<ProgramSummary[]>(`/v1/account/${id}/program-summary`, {
      internalOnly,
      showNonRecurring,
    }).then(this.handleResponse);
  }

  async getClientAccounts(): Promise<ClientAccount[]> {
    return (await this.get('/v1/clients').then(this.handleResponse)) as ClientAccount[];
  }

  async getExternalUsersForAccount(accountId: number | string): Promise<ExternalUser[]> {
    return await this.get<ExternalUser[]>(`/v1/account/${accountId}/client-access`).then(this.handleResponse);
  }

  async getAccountServicers(accountId: number | string): Promise<AccountServicingRole[]> {
    return await this.get<AccountServicingRole[]>(`/v1/account/${accountId}/servicers`).then(this.handleResponse);
  }

  async toggleServicerPublished(
    accountId: number | string,
    name: string,
    servicingRoleTitle: string,
    published: boolean,
  ): Promise<void> {
    await this.patch(`/v1/account/${accountId}/toggle-servicer-published`, {
      name,
      servicingRoleTitle,
      published,
    }).then(this.handleResponse);
  }

  async getPublicCompanyData(tickerSymbol: string): Promise<PublicCompanyData> {
    return (await this.get(`/v1/account/public-data`, { ts: tickerSymbol }).then(
      this.handleResponse,
    )) as PublicCompanyData;
  }

  async searchForPublicCompany(tickerSymbol: string, exchangeSymbol: string): Promise<PublicCompanySearchResult[]> {
    return this.get<PublicCompanySearchResult[]>(`/v1/account/public-company`, {
      ts: tickerSymbol,
      ex: exchangeSymbol,
    }).then(this.handleResponse);
  }
}

class ParentAccountService extends Service {
  async premiumByCoverageType(
    id: string | number,
    params?: Record<string, string | number | string[] | number[] | undefined>,
  ): Promise<MultiSeries> {
    return (await this.get(`/v1/parent/account/${id}/premium-by-coverage-type`, params).then(
      this.handleResponse,
    )) as MultiSeries;
  }

  async premiumByCarrier(
    id: string | number,
    params?: Record<string, string | number | string[] | number[] | undefined>,
  ): Promise<MultiSeries> {
    return (await this.get(`/v1/parent/account/${id}/premium-by-carrier`, params).then(
      this.handleResponse,
    )) as MultiSeries;
  }

  async premiumByCompany(
    id: string | number,
    params?: Record<string, string | number | string[] | number[] | undefined>,
  ): Promise<LabelValue[]> {
    return await this.get<LabelValue[]>(`/v1/parent/account/${id}/premium-by-company`, params).then(
      this.handleResponse,
    );
  }

  async policiesById(
    id: string | number,
    expired: boolean,
    internalOnly: boolean,
    hideUnpublishedRoles: boolean = false,
    coverageType?: string[],
  ): Promise<EpicPolicy[]> {
    return (await this.get(`/v1/parent/account/${id}/policies`, {
      expired,
      internalOnly,
      hideUnpublishedRoles,
      coverageType,
    }).then(this.handleResponse)) as EpicPolicy[];
  }

  async cardsById(
    id: string | number,
    params?: Record<string, string | number | string[] | number[] | undefined>,
  ): Promise<AccountCard[]> {
    return (await this.get(`/v1/parent/account/${id}/cards`, params).then(this.handleResponse)) as AccountCard[];
  }

  async GetClaimsByParentAccount(id: string | number): Promise<Claim[]> {
    return await this.get<Claim[]>(`/v1/parent/account/${id}/claims`).then(this.handleResponse);
  }

  async companies(
    parentAccountId: string | number,
    params?: Record<string, string | number | string[] | number[] | undefined>,
  ): Promise<ParentAccountCompany[]> {
    return await this.get<ParentAccountCompany[]>(`v1/parent/account/${parentAccountId}/companies`, params).then(
      this.handleResponse,
    );
  }

  async programExpiration(
    parentAccountId: string | number,
    startDate: string | null,
    endDate: string | null,
    external: boolean,
    coverageType?: string[],
  ): Promise<PortcoProgram[]> {
    return await this.get<PortcoProgram[]>(`v1/parent/account/${parentAccountId}/programs-expiring`, {
      startDate,
      endDate,
      external,
      coverageType,
    }).then(this.handleResponse);
  }

  async uploadExternalPolicies(
    parentAccountId: string | number,
    policies: ExternalPolicy[],
  ): Promise<ExternalPolicyUploadResponse> {
    return (await this.post(`/v1/parent/account/${parentAccountId}/external-policies`, policies).then(
      this.handleResponse,
    )) as ExternalPolicyUploadResponse;
  }
}

export interface PolicyAllParams {
  startDate: string;
  endDate: string;
  limit: number;
  offset: number;
  parentOnly: boolean;
  coverageType?: string[];
  topLevelIndustry?: string[];
  wholesaler?: string[];
  carrier?: string[];
  attachmentPointValueMin?: number;
  attachmentPointValueMax?: number;
  retentionMin?: number;
  retentionMax?: number;
  accountAttributes?: string[];
  showExternal?: boolean;
  activeOnly?: boolean;
  ownershipType?: string[];
  runoffFlag?: string[];
  peFlag?: string[];
  daysUntilExpiration?: number;
  favOnly?: boolean;
  sortField?: string;
  sortDirection?: string;
  accountStates?: string[];
  departments?: string[];
}

export interface PolicyBinderAIStatusBinder {
  documentID: number;
  documentName: string;
  aiPromptResultID: number;
  status: 'validated' | 'unvalidated';
}

export interface PolicyBinderAIStatusResponse {
  policyID: number;
  binders: PolicyBinderAIStatusBinder[];
}

class PolicyService extends Service {
  async all(data: PolicyAllParams, config?: AxiosRequestConfig): Promise<PoliciesResponse> {
    return (await this.get('/v1/policy', { ...data }, config).then(this.handleResponse)) as PoliciesResponse;
  }

  async stats(data: Pick<PolicyAllParams, 'startDate' | 'endDate'>, config?: AxiosRequestConfig): Promise<PolicyStats> {
    return (await this.get('/v1/policy/stats', { ...data }, config).then(this.handleResponse)) as PolicyStats;
  }

  async sublimits(policyId: string | number): Promise<PolicySublimit[]> {
    return (await this.get(`/v1/policies/${policyId}/sublimits`).then(this.handleResponse)) as PolicySublimit[];
  }

  async subretentions(policyId: string | number): Promise<PolicySubretention[]> {
    return (await this.get(`/v1/policies/${policyId}/subretentions`).then(this.handleResponse)) as PolicySubretention[];
  }

  async sublimitTypes(policyId: string | number, policyLineId: string | number): Promise<SubitemTypeRef[]> {
    return (await this.get(`/v1/policies/${policyId}/lines/${policyLineId}/sublimit-types`).then(this.handleResponse)) as SubitemTypeRef[];
  }

  async subretentionTypes(policyId: string | number, policyLineId: string | number): Promise<SubitemTypeRef[]> {
    return (await this.get(`/v1/policies/${policyId}/lines/${policyLineId}/subretention-types`).then(this.handleResponse)) as SubitemTypeRef[];
  }

  async children(policyId: string | number): Promise<EpicPolicy[]> {
    return (await this.get(`/v1/policy/${policyId}/children`).then(this.handleResponse)) as EpicPolicy[];
  }

  async binderAIStatus(policyID: number): Promise<PolicyBinderAIStatusResponse> {
    return (await this.get(`/v1/policies/${policyID}/binder-ai-status`).then(this.handleResponse)) as PolicyBinderAIStatusResponse;
  }
}

class BenchmarkService extends Service {
  async account(accountId: number | string): Promise<AccountBenchmark> {
    return (await this.get(`/v1/benchmark/account/${accountId}`).then(this.handleResponse)) as AccountBenchmark;
  }

  async dno(
    ownershipType: string,
    accountAttributeIDIncluded: number[],
    accountAttributeIDExcluded: number[],
    industries: number[],
  ): Promise<DOBenchmark[]> {
    return (await this.get('/v1/benchmark/dno', {
      ownershipType,
      accountAttributeIDIncluded,
      accountAttributeIDExcluded,
      industries,
    }).then(this.handleResponse)) as DOBenchmark[];
  }

  async cyber(
    ownershipType: string,
    accountAttributeIDIncluded: number[],
    accountAttributeIDExcluded: number[],
  ): Promise<CyberBenchmark[]> {
    return (await this.get('/v1/benchmark/cyber', {
      ownershipType,
      accountAttributeIDIncluded,
      accountAttributeIDExcluded,
    }).then(this.handleResponse)) as CyberBenchmark[];
  }

  async fiduciary(
    accountAttributeIDIncluded: number[],
    accountAttributeIDExcluded: number[],
  ): Promise<FiduciaryBenchmark[]> {
    return (await this.get('/v1/benchmark/fiduciary', {
      accountAttributeIDIncluded,
      accountAttributeIDExcluded,
    }).then(this.handleResponse)) as FiduciaryBenchmark[];
  }

  async epl(accountAttributeIDIncluded: number[], accountAttributeIDExcluded: number[]): Promise<EPLBenchmark[]> {
    return await this.get<EPLBenchmark[]>('/v1/benchmark/epl', {
      accountAttributeIDIncluded,
      accountAttributeIDExcluded,
    }).then(this.handleResponse);
  }

  async crime(accountAttributeIDIncluded: number[], accountAttributeIDExcluded: number[]): Promise<CrimeBenchmark[]> {
    return await this.get<CrimeBenchmark[]>('/v1/benchmark/crime', {
      accountAttributeIDIncluded,
      accountAttributeIDExcluded,
    }).then(this.handleResponse);
  }
}

export interface QueueStats {
  pending: number;
  oldestPendingAt: string | null;
  backlogAgeSeconds: number;
}

/** The Policy Explorer rebuild runs through a worker, so it reports in-flight work too. */
export interface PolicyExplorerQueueStats extends QueueStats {
  processing: number;
  stuck: number;
  completedRetainedApprox: number;
  fullRebuildPending: boolean;
}

export interface QueueStatsResponse {
  accountSearchIndex: QueueStats;
  policyExplorerCache: PolicyExplorerQueueStats;
}

class CacheService extends Service {
  async flush(): Promise<void> {
    return await this.post('/v1/cache/flush', {});
  }

  async queueStats(): Promise<QueueStatsResponse> {
    return (await this.get('/v1/queues/stats').then(this.handleResponse)) as QueueStatsResponse;
  }
}

class UserAccountService extends Service {
  async create(accountID: number): Promise<string> {
    return await this.post('/v1/user-account', { accountID });
  }

  async remove(accountID: number): Promise<string> {
    return await this.delete(`/v1/user-account/${accountID}`);
  }
}

export interface Favorite {
  id: number;
  userSub: string;
  service: string;
  objectId: string;
}

class FavoriteService extends Service {
  async list(): Promise<Favorite[]> {
    return await this.get<Favorite[]>('/v1/favorite').then(this.handleResponse);
  }

  async create(service: string, objectId: number | string): Promise<string> {
    return await this.post('/v1/favorite', { service: service, objectId: String(objectId) });
  }

  async remove(service: string, objectId: number | string): Promise<string> {
    return await this.delete(`/v1/favorite/${service}/${objectId}`);
  }
}

interface UserFeedback {
  details: string;
  screenshot: string;
  url: string;
}

class UserFeedbackService extends Service {
  async SubmitFeedback(feedback: UserFeedback): Promise<void> {
    await this.post('/v1/user-feedback/submit', feedback);
  }
}

class PolicyOverrideService extends Service {
  private static clearEmptyValues(override: any): void {
    for (const [key, value] of Object.entries(override)) {
      if (value === '' && key !== 'sideAOverride') {
        override[key] = null;
      }
    }
  }

  async GetCurrentOverride(policyID: string): Promise<CurrentPolicyOverride> {
    return (await this.get<CurrentPolicyOverride>(`/v1/policies/${policyID}/override`)).data;
  }

  async SetOverride(uniqPolicy: string, override: CurrentPolicyOverride): Promise<void> {
    PolicyOverrideService.clearEmptyValues(override.policyOverride);
    await this.post(`/v1/policies/${uniqPolicy}/override`, override);
  }

  async saveOverride(policyId: string, policyOverride: PolicyOverride): Promise<void> {
    await this.post(`/v1/policies/${policyId}/override`, { policyOverride });
  }

  async GetOverrideHistory(uniqPolicy: string | number): Promise<AuditLog[]> {
    return (await this.get<AuditLog[]>(`/v1/policies/${uniqPolicy}/override-history`)).data;
  }

  async GetCurrentLineOverride(policyId: string, policyLineId: string): Promise<CurrentPolicyLineOverride> {
    return (await this.get<CurrentPolicyLineOverride>(`/v1/policies/${policyId}/override/${policyLineId}`)).data;
  }

  async SetLineOverride(policyId: string, policyLineId: string, override: CurrentPolicyLineOverride): Promise<void> {
    PolicyOverrideService.clearEmptyValues(override.policyOverride);
    await this.post(`/v1/policies/${policyId}/override/${policyLineId}`, override);
  }

  async saveLineOverride(policyId: string, lineId: string, policyLineOverride: PolicyLineOverride): Promise<void> {
    await this.post(`/v1/policies/${policyId}/override/${lineId}`, { policyOverride: policyLineOverride });
  }

  async saveOverrideBatch(
    policyId: string,
    payload: { policyOverride?: PolicyOverride; lineOverrides?: Record<string, PolicyLineOverride> },
  ): Promise<void> {
    await this.post(`/v1/policies/${policyId}/override-batch`, payload);
  }

  async GetLineOverrideHistory(uniqPolicy: string, uniqLine: string): Promise<AuditLog[]> {
    return (await this.get<AuditLog[]>(`/v1/policies/${uniqPolicy}/override-history/${uniqLine}`)).data;
  }
}

class RevenueService extends Service {
  async getByAccount(startDate: DateTime, endDate: DateTime): Promise<LabelValue[]> {
    const dateRange = { startDate: startDate.toISODate(), endDate: endDate.toISODate() };
    return (await this.get('/v1/revenue', { type: 'account', ...dateRange }).then(this.handleResponse)) as LabelValue[];
  }

  async getByCoverageType(startDate: DateTime, endDate: DateTime, limit = 20): Promise<MultiSeries> {
    const dateRange = { startDate: startDate.toISODate(), endDate: endDate.toISODate() };
    type revenueByCoverageTypeResult = { coverageType: string; agencyName: string; value: number; totalValue: number };
    const result = (await this.get('/v1/revenue', { type: 'coverageType', ...dateRange }).then(
      this.handleResponse,
    )) as revenueByCoverageTypeResult[];
    const coverageTypes = [...new Set(result.map((r) => r.coverageType))].slice(0, limit);
    const agencyNames = [...new Set(result.map((r) => r.agencyName))];
    const categories = [{ category: coverageTypes.map((coverageType) => ({ label: coverageType })) }];
    const valueOf = (coverageType: string, agencyName: string): { value?: number } => {
      const data = result.find((r) => r.coverageType === coverageType && r.agencyName === agencyName);
      if (data) {
        return { value: data.value > 0 ? data.value : undefined };
      }
      return { value: undefined };
    };
    const dataset = agencyNames.map((agencyName) => ({
      seriesname: agencyName,
      data: coverageTypes.flatMap((coverageType) => valueOf(coverageType, agencyName)),
    }));
    return { categories, dataset };
  }

  async getByCarrier(startDate: DateTime, endDate: DateTime): Promise<LabelValue[]> {
    const dateRange = { startDate: startDate.toISODate(), endDate: endDate.toISODate() };
    return (await this.get('/v1/revenue', { type: 'carrier', ...dateRange }).then(this.handleResponse)) as LabelValue[];
  }

  async getByDepartment(startDate: DateTime, endDate: DateTime): Promise<LabelValue[]> {
    const dateRange = { startDate: startDate.toISODate(), endDate: endDate.toISODate() };
    return (await this.get('/v1/revenue', { type: 'department', ...dateRange }).then(
      this.handleResponse,
    )) as LabelValue[];
  }
}

class QuoteService extends Service {
  async GetQuotePrograms(accountId: string): Promise<QuoteProgram[]> {
    return (await this.get<QuoteProgram[]>(`/v1/account/${accountId}/quote-program`)).data;
  }

  async CreateQuoteProgram(program: QuoteProgram): Promise<void> {
    await this.post<QuoteProgram>(`/v1/account/${program.accountID}/quote-program`, program);
  }

  async CreateFullQuoteProgram(program: QuoteProgram, layers: QuoteLayer[], quotes: Quote[]): Promise<string> {
    return await this.post<string>('/v1/quote-program/upload', {
      program,
      layers: [...layers],
      quotes: [...quotes],
    }).then(this.handleInsertResponse);
  }

  async UpdateQuoteProgram(programId: number | string, program: QuoteProgram): Promise<void> {
    await this.post(`/v1/quote-program/update/${programId}`, program).then(this.handleResponse);
  }

  async UpdateLayer(quoteId: number, quoteLayer: QuoteLayer): Promise<void> {
    await this.put(`/v1/quote-program/${quoteId}/layer/${quoteLayer.layerId}`, quoteLayer).then(this.handleResponse);
  }

  async DeleteQuoteProgram(accountId: string, quoteProgramId: string): Promise<void> {
    await this.delete(`/v1/account/${accountId}/quote-program/${quoteProgramId}`);
  }

  async GetQuotes(programId: number | string): Promise<QuoteProgram> {
    return (await this.get<QuoteProgram>(`/v1/quote-program/${programId}`)).data;
  }

  async CreateQuote(quote: Quote): Promise<string> {
    return await this.post<string>('/v1/quote', quote).then(this.handleInsertResponse);
  }

  async DeleteQuote(quoteId: number | string): Promise<void> {
    await this.delete(`/v1/quote/${quoteId}`).then(this.handleResponse);
  }

  async UpdateQuote(quoteId: number, quote: Quote): Promise<void> {
    await this.put(`/v1/quote/${quoteId}`, quote).then(this.handleResponse);
  }

  async SequenceQuotes(request: QuoteSequenceRequest): Promise<void> {
    return this.patch('/v1/quote/sequence', request).then(this.handleResponse);
  }

  async CreateQuoteLayer(layer: QuoteLayer, quoteId: number): Promise<string> {
    return await this.post<string>(`/v1/quote-program/${quoteId}/layer`, layer).then(this.handleInsertResponse);
  }

  async UpdateQuoteLayer(layer: QuoteLayer, quoteProgramId: number): Promise<void> {
    await this.put(`/v1/quote-program/${quoteProgramId}/layer/${layer.layerId}`, layer).then(this.handleResponse);
  }

  async DeleteQuoteLayer(programId: string, layerId: number | undefined): Promise<void> {
    await this.delete(`/v1/quote-program/${programId}/layer/${layerId}`).then(this.handleResponse);
  }
}

class AuthService extends Service {
  async getUserInfo(): Promise<User> {
    return await this.get<User>('/v1/.auth/me').then(this.handleResponse);
  }

}

class UmbrellaBenchmarkingService extends Service {
  limitParams(limit: (number | string)[]): Record<string, number | string> {
    const limitParams = {} as Record<string, number | string>;
    if (limit.length === 2) {
      limitParams['limitMin'] = limit[0];
      if (limit[1] !== '' && limit[1] !== 0) {
        limitParams['limitMax'] = limit[1];
      }
    }
    return limitParams;
  }

  async LimitsByCarrier(
    industry: string[],
    ownership: string[],
    limit: (number | string)[],
  ): Promise<UmbrellaCarrierBenchmark[] | null> {
    const limitParams = this.limitParams(limit);
    return (await this.get('/v1/benchmarking/umbrellalimitsbyindustry', { ...limitParams, industry, ownership }).then(
      this.handleResponse,
    )) as UmbrellaCarrierBenchmark[] | null;
  }

  async TotalLimits(
    industry: string[],
    ownership: string[],
    limit: (number | string)[],
  ): Promise<UmbrellaTotalLimit[]> {
    const limitParams = this.limitParams(limit);
    return (await this.get('/v1/benchmarking/umbrellatotallimits', { ...limitParams, industry, ownership }).then(
      this.handleResponse,
    )) as UmbrellaTotalLimit[];
  }
}

class PermissionService extends Service {
  async AddPermission(perm: string, sub: string): Promise<void> {
    await this.post(`/v1/admin/permissions/add/${sub}/${perm}`, {}).then(this.handleResponse);
  }

  async DeletePermission(perm: string, sub: string): Promise<void> {
    await this.delete(`/v1/admin/permissions/delete/${sub}/${perm}`).then(this.handleResponse);
  }

  async GetUserPermissionsV2(includeDeleted: boolean = false): Promise<Array<UserPermissionDetails>> {
    return await this.get<UserPermissionDetails[]>(`/v1/admin/permissions/users?includeDeleted=${includeDeleted}`).then(this.handleResponse);
  }

  async GetAllUserRolesV2(): Promise<any> {
    return await this.get<any>('/v1/admin/permissions/roles').then(this.handleResponse);
  }

  async getAppRoles(): Promise<AppRole[]> {
    return await this.get<AppRole[]>('/v1/admin/permissions/app-roles').then(this.handleResponse);
  }

  async CreateUserWithAccounts(
    name: string,
    email: string,
    role: string,
    accounts: Array<AccountInfo>,
    permissions: Array<string>,
    phone?: string,
    mfaEnabled?: boolean,
  ): Promise<void> {
    await this.post('/v1/admin/permissions/user', { name, email, role, accounts, permissions, phone, mfaEnabled }).then(
      this.handleResponse,
    );
  }

  async CreateMarketUser(
    name: string,
    email: string,
    carriers: number[],
    wholesalers: number[],
    phone?: string,
    mfaEnabled?: boolean,
  ): Promise<void> {
    await this.post('/v1/admin/permissions/user', {
      name,
      email,
      role: 'market',
      carriers,
      wholesalers,
      phone,
      mfaEnabled,
    }).then(this.handleResponse);
  }

  async DeleteUser(sub: string): Promise<void> {
    await this.delete(`/v1/admin/permissions/user/${sub}`).then(this.handleResponse);
  }

  async RestoreUser(sub: string): Promise<void> {
    await this.post(`/v1/admin/permissions/user/${sub}/restore`, {}).then(this.handleResponse);
  }

  async getAllAccounts(): Promise<AccountInfo[]> {
    return ((await this.get('/v1/admin/accounts').then(this.handleResponse)) as AccountInfo[]) ?? [];
  }

  async updateUser(
    sub: string,
    name: string,
    email: string,
    accounts: AccountInfo[],
    carriers: number[],
    wholesalers: number[],
    phone?: string,
    mfaEnabled?: boolean,
  ): Promise<void> {
    await this.patch(`/v1/admin/permissions/user/${sub}`, {
      accounts,
      email,
      name,
      carriers,
      wholesalers,
      phone,
      mfaEnabled,
    }).then(this.handleResponse);
  }

  async updateUserPassword(sub: string, password: string): Promise<void> {
    await this.post(`/v1/admin/permissions/user/${sub}`, { password }).then(this.handleResponse);
  }

  async getAllowedPermissionsByRole(): Promise<AllowedPermissionsByRole[]> {
    return (
      ((await this.get('/v1/admin/permissions/allowed-permissions-by-role').then(
        this.handleResponse,
      )) as AllowedPermissionsByRole[]) ?? []
    );
  }
}

class ProgramService extends Service {
  async getProgramInfoById(id: number): Promise<ProgramInfo> {
    return this.get<ProgramInfo>(`/v1/programs/${id}`).then(this.handleResponse);
  }

  async getProgramPolicy(programId: number, policyId: number): Promise<ProgramPolicy> {
    return this.get<ProgramPolicy>(`/v2/programs/${programId}/policies/${policyId}`).then(this.handleResponse);
  }

  async getProgramPolicyLine(programId: number, policyLineId: number): Promise<PolicyLine> {
    return this.get<PolicyLine>(`/v2/programs/${programId}/lines/${policyLineId}`).then(this.handleResponse);
  }
}

class PolicySourceService extends Service {
  async getPolicySourceById(id: number): Promise<PolicySource> {
    return this.get<PolicySource>(`/v1/policy-source/${id}`).then(this.handleResponse);
  }

  async list(): Promise<PolicySource[]> {
    return this.get<PolicySource[]>('/v1/policy-source').then(this.handleResponse);
  }
}

class UserProfileService extends Service {
  async updateUserProfile(request: UserProfilePatchRequest): Promise<boolean> {
    return await this.patch('/v1/user-profile', request).then(this.handleResponse);
  }

  async getUserProfile(): Promise<UserProfile> {
    return await this.get<UserProfile>('/v1/user-profile').then(this.handleResponse);
  }

  async updateHomepagePreference(request: HomepagePreferencePatchRequest): Promise<boolean> {
    return await this.patch('/v1/user-profile/homepage', request).then(this.handleResponse);
  }
}

class TableauService extends Service {
  constructor() {
    super();
    return new Proxy(this, {
      get(target, prop) {
        const method = target[prop];
        if (typeof method === 'function') {
          return async (...args: any[]) => {
            // add X-Tableau-Auth header to all requests
            const tableauUserId = sessionStorage.getItem('tableauUserId');
            if (tableauUserId) {
              target.axiosInstance.defaults.headers['X-Tableau-User-Id'] = tableauUserId;
            }

            return await method.apply(target, args);
          };
        }
      },
    });
  }

  async createDashboard(name: string, url: string, tableauTypeId: number, accountId: number | null) {
    const body: CreateTableauRequest = {
      name,
      dashboardUrl: url,
      tableauTypeId,
      accountId,
    };
    return this.post(`/v2/tableau`, body).then(this.handleTableauResponse);
  }

  async updateDashboard(id: number, name: string, url: string, tableauTypeId: number, accountId: number | null) {
    const body: UpdateTableauRequest = {
      name,
      dashboardUrl: url,
      tableauTypeId,
      accountId,
    };
    return this.put(`/v2/tableau/${id}`, body).then(this.handleTableauResponse);
  }

  async getDashboards(accountId: number): Promise<TableauDashboard[]> {
    return await this.get<TableauDashboard[]>(`/v1/account/${accountId}/tableau`).then(this.handleTableauResponse);
  }

  async getAllDashboards(): Promise<TableauDashboard[]> {
    return await this.get<TableauDashboard[]>(`/v2/tableau`).then(this.handleTableauResponse);
  }

  async getDashboardTrustedAuthUrl(dashboardURL: string): Promise<string> {
    return await this.get<string>(`/v2/tableau/trusted-auth-dashboard-url`, { dashboardURL }).then(
      this.handleTableauResponse,
    );
  }

  async getDashboardTrustedAuthUrlById(id: number | string): Promise<string> {
    return await this.get<string>(`/v2/tableau/trusted-auth-dashboard-url`, { id }).then(this.handleTableauResponse);
  }

  async togglePinned(tableauId: number) {
    return await this.patch(`/v2/tableau/${tableauId}/toggle-pinned`, tableauId).then(this.handleTableauResponse);
  }

  async togglePublished(tableauId: number) {
    return await this.patch(`/v2/tableau/${tableauId}/toggle-published`, tableauId).then(this.handleTableauResponse);
  }

  async deleteDashboard(accountId: number, tableauId: number) {
    return this.delete(`/v1/account/${accountId}/tableau/${tableauId}`).then(this.handleTableauResponse);
  }

  async deleteDashboardV2(tableauId: number) {
    return this.delete(`/v2/tableau/${tableauId}`).then(this.handleTableauResponse);
  }

  async getTableauTypes(): Promise<TableauType[]> {
    return this.get<TableauType[]>(`/v2/tableau-refs/tableau-types`).then(this.handleResponse);
  }

  async getAnalyticsTypes(): Promise<TableauType[]> {
    return this.get<TableauType[]>(`/v2/analytics/types`).then(this.handleResponse);
  }
}

class AnalyticsService extends Service {
  async getAccountProgramData(accountId: number | string): Promise<AccountProgramData> {
    return this.get<AccountProgramData>('/v2/analytics/program-data', { accountId }).then(this.handleResponse);
  }

  async getAccountFinancials(capIQId: number | string, asOfDate: string): Promise<AccountFinancials> {
    return this.get<AccountFinancials>(`/v2/analytics/${capIQId}/financials`, { asOfDate }).then(this.handleResponse);
  }
}

class CyberCubeService extends Service {
  async createAccount(accountId: number, request: Partial<CompanyExposureAccount>): Promise<unknown> {
    return await this.post(`/v2/accounts/${accountId}/cybercube/accounts`, request).then(this.handleResponse);
  }

  async getCompanyExposureAccount(accountId: number, cyberCubeId: string): Promise<CompanyExposureAccount> {
    return this.get<CompanyExposureAccount>(
      `/v2/accounts/${accountId}/cybercube/company-exposure/accounts/${cyberCubeId}`,
    ).then(this.handleResponse);
  }

  async updateCompanyExposureAccount(
    accountId: number,
    cyberCubeId: string,
    companyExposureData: Partial<CompanyExposureAccount>,
  ): Promise<unknown> {
    return this.put(
      `/v2/accounts/${accountId}/cybercube/company-exposure/accounts/${cyberCubeId}`,
      companyExposureData,
    ).then(this.handleResponse);
  }

  async startExposureAnalysis(accountId: number, cyberCubeId: string): Promise<void> {
    await this.post(`/v2/accounts/${accountId}/cybercube/company-exposure/accounts/${cyberCubeId}/analysis`, {}).then(
      this.handleResponse,
    );
  }

  async getLatestExposureAnalysisRun(accountId: number): Promise<AnalysisRun[]> {
    return this.get<AnalysisRun[]>(`/v2/accounts/${accountId}/cybercube/company-exposure/analysis/status`).then(
      this.handleResponse,
    );
  }

  async getCompanyActualLimitsAndRetention(accountId: number): Promise<CompanyActualLimitsAndRetention> {
    return this.get<CompanyActualLimitsAndRetention>(
      `/v2/accounts/${accountId}/cybercube/actual-limits-and-retention`,
    ).then(this.handleResponse);
  }

  async getCurrentVSAggregateExposure(accountId: number): Promise<CurrentVSAggregateExposure[]> {
    return this.get<CurrentVSAggregateExposure[]>(
      `/v2/accounts/${accountId}/cybercube/current-vs-aggregate-exposure`,
    ).then(this.handleResponse);
  }

  async getCyberModelDetailsByAccount(accountId: number): Promise<CyberModelDetails[]> {
    return this.get<CyberModelDetails[]>(`/v2/accounts/${accountId}/cybercube/model-details`).then(this.handleResponse);
  }

  async getAllCyberModelDetails(): Promise<CyberModelDetails[]> {
    return this.get<CyberModelDetails[]>(`/v2/cybercube/models`).then(this.handleResponse);
  }

  async getAccountsWithExistingModels(): Promise<number[]> {
    return this.get<number[]>(`/v2/cybercube/models/accounts`).then(this.handleResponse);
  }

  async publishCyberAnalysisRun(runId: number): Promise<void> {
    await this.post(`/v2/cybercube/analysis-run/${runId}/publish`, {}).then(this.handleResponse);
  }

  async unpublishCyberAnalysisRun(runId: number): Promise<void> {
    await this.post(`/v2/cybercube/analysis-run/${runId}/unpublish`, {}).then(this.handleResponse);
  }
}

export const carrierService = new CarrierService();
export const favoriteService = new FavoriteService();
export const referenceService = new ReferenceService();
export const universalSearchService = new UniversalSearchService();
export const accountService = new AccountService();
export const parentAccountService = new ParentAccountService();
export const userAccountService = new UserAccountService();
export const benchmarkService = new BenchmarkService();
export const userFeedbackService = new UserFeedbackService();
export const policyOverrideService = new PolicyOverrideService();
export const revenueService = new RevenueService();
export const policyService = new PolicyService();
export const wholesalerGroupService = new WholesalerGroupService();
export const wholesalerService = new WholesalerService();
export const lineStatusService = new LineStatusService();
export const volatilityModelService = new VolatilityModelService();
export const stockDropModelService = new StockDropModelService();
export const personalService = new PersonalService();
export const quoteService = new QuoteService();
export const authService = new AuthService();
export const umbrellaBenchmarkingService = new UmbrellaBenchmarkingService();
export const cacheService = new CacheService();
export const permissionService = new PermissionService();
export const programService = new ProgramService();
export const policySourceService = new PolicySourceService();
export const userProfileService = new UserProfileService();
export const tableauService = new TableauService();
export const analyticsService = new AnalyticsService();
export const cyberCubeService = new CyberCubeService();
